import { ErrorCode, EvaluationDetails, FlagValue, useOpenFeatureClient } from '@openfeature/react-sdk'
import { Card, Icon, Tag, TagColor, YStack } from '@shinetools/lumen-react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useCallback, useId, useState } from 'react'

import { evaluateRemoteFeatureFlag } from '@features/remoteFeatureFlags/evaluateRemoteFeatureFlag'

import { DetailRow } from '../DetailRow'
import { RemoteFeatureFlagStatus } from './enums/remoteFeatureFlagStatus'
import * as Styled from './styles'

interface RemoteFeatureFlagAccordionProps {
  flagKey: string
}

interface FlagSummary {
  label: string
  status: RemoteFeatureFlagStatus
}

const tagColors: Record<RemoteFeatureFlagStatus, TagColor> = {
  [RemoteFeatureFlagStatus.Error]: 'warning',
  [RemoteFeatureFlagStatus.False]: 'danger',
  [RemoteFeatureFlagStatus.NotFound]: 'neutral',
  [RemoteFeatureFlagStatus.True]: 'success',
  [RemoteFeatureFlagStatus.Value]: 'blue',
}

const formatFlagValue = (value: unknown): string => JSON.stringify(value) ?? String(value)

const getFlagSummary = ({ errorCode, value }: EvaluationDetails<FlagValue>): FlagSummary => {
  if (errorCode === ErrorCode.FLAG_NOT_FOUND) {
    return { label: 'not found', status: RemoteFeatureFlagStatus.NotFound }
  }

  if (errorCode) {
    return { label: errorCode, status: RemoteFeatureFlagStatus.Error }
  }

  if (typeof value === 'boolean') {
    return { label: String(value), status: value ? RemoteFeatureFlagStatus.True : RemoteFeatureFlagStatus.False }
  }

  return {
    label: typeof value === 'object' ? 'object' : formatFlagValue(value),
    status: RemoteFeatureFlagStatus.Value,
  }
}

export const RemoteFeatureFlagAccordion = ({ flagKey }: RemoteFeatureFlagAccordionProps) => {
  const client = useOpenFeatureClient()
  const [isOpen, setIsOpen] = useState(false)
  const bodyId = useId()
  const details = evaluateRemoteFeatureFlag(client, flagKey)
  const { label, status } = getFlagSummary(details)

  const handleHeaderClick = useCallback(() => {
    setIsOpen((wasOpen) => !wasOpen)
  }, [])

  return (
    <Card variant="outlined">
      <YStack align="stretch">
        <Styled.AccordionHeader
          $status={status}
          aria-controls={bodyId}
          aria-expanded={isOpen}
          onClick={handleHeaderClick}
          type="button"
        >
          <Styled.StatusDot $status={status} />
          <code>{flagKey}</code>
          <Tag color={tagColors[status]} label={label} />
          <Icon icon={isOpen ? ChevronUp : ChevronDown} />
        </Styled.AccordionHeader>
        <div hidden={!isOpen} id={bodyId}>
          <YStack gap={8} padding={16}>
            <DetailRow label="Value">
              {formatFlagValue(details.value)}
              {details.errorCode && ' (default)'}
            </DetailRow>
            <DetailRow label="Reason">{details.reason ?? '—'}</DetailRow>
            <DetailRow label="Variant">{details.variant ?? '—'}</DetailRow>
            <DetailRow label="Error code">{details.errorCode ?? '—'}</DetailRow>
            {details.errorMessage && <DetailRow label="Error message">{details.errorMessage}</DetailRow>}
            <DetailRow label="Metadata">
              <code>{formatFlagValue(details.flagMetadata)}</code>
            </DetailRow>
          </YStack>
        </div>
      </YStack>
    </Card>
  )
}
